import React, { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/";

type GiftCardProductFields = {
  type?: string;
  gift_card_restrict_facility?: boolean;
  gift_card_expiry_type?: string;
  gift_card_expiry_years?: number;
};

interface IGiftCardProductSettingsCardProps<T extends GiftCardProductFields> {
  product: T;
  setProduct: Dispatch<SetStateAction<T>>;
  showFacilityRestriction: boolean;
  disabled?: boolean;
  onToggleChange: (e: ChangeEvent<HTMLInputElement>, product: T, setProduct: Dispatch<SetStateAction<T>>) => void;
}

export default function GiftCardProductSettingsCard<T extends GiftCardProductFields>({
  product,
  setProduct,
  showFacilityRestriction,
  disabled = false,
  onToggleChange,
}: IGiftCardProductSettingsCardProps<T>) {
  const { t } = useTranslation();

  if (product.type !== "Gift Card") {
    return null;
  }

  return (
    <Card
      title={t("Gift Card")}
      subtitle={t("Configure expiry and facility restrictions for gift cards sold from this product.")}
    >
      <Card.Section>
        <FormLayout>
          {showFacilityRestriction ? (
            <FormLayout.Group>
              <Toggle
                id="gift_card_restrict_facility"
                checked={product.gift_card_restrict_facility}
                onChange={e => onToggleChange(e, product, setProduct)}
                labelRight={t("Restrict Gift Card to Facility")}
                labelSupport={t("Gift card can only be used at this facility.")}
                disabled={disabled}
              />
            </FormLayout.Group>
          ) : null}
          <FormLayout.Group>
            <Select
              label={t("Gift card expiry")}
              defaultValue={product.gift_card_expiry_type ?? "inherit"}
              onChange={(value: string) => setProduct(prev => ({ ...prev, gift_card_expiry_type: value }))}
              disabled={disabled}
            >
              <Select.Option value="inherit">{t("Use facility default")}</Select.Option>
              <Select.Option value="no_expiry">{t("No Expiration")}</Select.Option>
              <Select.Option value="one_year">{t("One Year")}</Select.Option>
              <Select.Option value="end_of_year">{t("End of Year")}</Select.Option>
              <Select.Option value="custom_years">{t("Custom Years")}</Select.Option>
            </Select>
            {product.gift_card_expiry_type === "custom_years" ? (
              <Input
                type="number"
                label={t("Gift card expiry years")}
                value={product.gift_card_expiry_years ?? 5}
                onChange={e => setProduct(prev => ({ ...prev, gift_card_expiry_years: Number(e.target.value) }))}
                disabled={disabled}
                min={1}
                max={99}
              />
            ) : null}
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>
    </Card>
  );
}
